// TeamSelectById.tsx  (drop-in replacement)

import { Autocomplete, Avatar, AvatarGroup, Box, FormControl, TextField, useTheme } from '@mui/material'
import { renderTeamOption } from 'components/Team/renderOptions'
import React, { useMemo } from 'react'

/** Expect: value = string[] of user ids (user_uuid or id) */
interface Props {
  name: string
  value: string[] // <-- ID array
  options: any[] // users (objects with user_uuid/id, name, image_512, role, etc.)
  displayLabel: string
  onChange: (e: { target: { name?: string; value: string[] } }) => void // <-- emits ID array
  inputRef?: React.RefObject<HTMLInputElement>
  disabled?: boolean
  sx?: object
}

const TeamSelectById: React.FC<Props> = ({
  name,
  value = [],
  options = [],
  displayLabel,
  onChange,
  inputRef,
  disabled,
  sx,
}) => {
  const theme = useTheme()

  // Map incoming IDs -> option objects for Autocomplete "value"
  const byId = useMemo(() => {
    return new Map(options.map(u => [(u.user_uuid ?? u.id) as string, u]))
  }, [options])

  const selectedObjects = useMemo(() => {
    return (value ?? []).map(id => byId.get(id)).filter(Boolean)
  }, [value, byId])

  const handleChange = (_event: any, newOptions: any[]) => {
    // emit only IDs
    const newIds = Array.from(new Set((newOptions ?? []).map(u => u?.user_uuid ?? u?.id).filter(Boolean)))
    onChange({ target: { name, value: newIds } })
  }

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <FormControl fullWidth>
        <Autocomplete
          multiple
          disableCloseOnSelect
          filterSelectedOptions
          options={options}
          value={selectedObjects}
          onChange={handleChange}
          getOptionLabel={option =>
            option?.name ||
            `${option?.firstName ?? ''} ${option?.lastName ?? ''}`.trim() ||
            (option?.user_uuid ?? option?.id ?? '')
          }
          isOptionEqualToValue={(option, val) => (option?.user_uuid ?? option?.id) === (val?.user_uuid ?? val?.id)}
          renderOption={(props, option, state) => renderTeamOption(props, option, state, theme)}
          renderTags={(tagValue, getTagProps) => (
            <AvatarGroup
              max={4}
              spacing="small"
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& .MuiAvatar-root': { mr: 0.5, height: 28, width: 28 },
              }}
            >
              {tagValue.map((u, index) => {
                const { key: _key, onDelete: _onDelete, ...rest } = getTagProps({ index })
                return (
                  <Avatar
                    key={u.user_uuid ?? u.id}
                    alt={u.name}
                    src={u.image_512}
                    sx={{ height: 32, width: 32 }}
                    {...rest}
                  />
                )
              })}
            </AvatarGroup>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={displayLabel}
              placeholder="Search by name or role"
              inputRef={inputRef}
              InputProps={{ ...params.InputProps, endAdornment: null }}
              sx={{
                maxHeight: '45px',
                '& .MuiAutocomplete-inputRoot': { maxHeight: '45px', height: '45px' },
              }}
            />
          )}
          disabled={disabled}
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              m: 0,
              p: 0,
              pl: 1,
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
            },
          }}
        />
      </FormControl>
    </Box>
  )
}

export default TeamSelectById
